import {
  storefrontAppearanceAtom,
  storefrontAppearanceReadyAtom,
} from "@/atoms/storefront-appearance";
import { storefrontAppearanceSource } from "@/domain/storefront-appearance";
import { getMerchantId } from "@/lib/company-storage";
import { getDefaultStore } from "jotai";

/**
 * APPEARANCE FETCH, WITH PRIORITY
 *
 * Outside the React cycle on purpose.
 *
 * As a `useEffect`, read could only start after mount — and on a COLD visit
 * after full bootstrap, because merchant id appears when `/by-subdomain`
 * returns. Storefront color, which unlocks the first screen, was asked LAST.
 *
 * As a free function it fires at both moments there is something new to know:
 * `main.tsx` before first render (return visit: palette before first paint) and
 * `App` when the merchant responds (cold visit: jumps ahead of catalog/table).
 *
 * What priority does NOT fix: on a cold visit there is no palette before
 * `/by-subdomain` — the first screen uses defaults and the merchant palette
 * enters revealed later. Writes jotai default store (no `<Provider>`).
 */

/**
 * Merchant whose appearance was already read. Avoids a second fetch when both
 * call sites share the same merchant (return visit: `main.tsx` then `App`).
 */
let loadedFor: string | null = null;

/**
 * Fetches storefront appearance once the merchant is known.
 *
 * WITHOUT a resolved merchant the ready flag still rises — on a cold visit the
 * id only exists after `/by-subdomain`, and holding the skeleton until then
 * leaves the customer on a spinner while the page could already draw. Default
 * palette is the best answer now; the merchant's arrives REVEALED via iris
 * (see `useStorefrontColors`).
 */
export async function loadStorefrontAppearance(): Promise<void> {
  const merchantId = getMerchantId();
  if (!merchantId) {
    settleStorefrontAppearance();
    return;
  }
  if (loadedFor === merchantId) return;
  loadedFor = merchantId;

  const store = getDefaultStore();

  try {
    store.set(
      storefrontAppearanceAtom,
      await storefrontAppearanceSource.get(merchantId),
    );
  } catch (error) {
    console.info("[storefront-appearance] leitura falhou (silencioso)", error);
  } finally {
    store.set(storefrontAppearanceReadyAtom, true);
  }
}

/**
 * "Nothing more can be known about appearance" — releases the first screen.
 *
 * Called at bootstrap end in ANY outcome so "Carregando..." cannot hang forever
 * when the store is missing, subdomain fails, or the network drops. Idempotent.
 */
export function settleStorefrontAppearance(): void {
  getDefaultStore().set(storefrontAppearanceReadyAtom, true);
}

/** Tests only: forget the merchant already read between cases. */
export function resetStorefrontAppearanceLoader(): void {
  loadedFor = null;
}
